<template>
  <div class="datepicker">
    <div class="header">
      <div class="navigation-header">
        <button class="nav-arrow prev-arrow" @click="navigatePrevious">
          <span>←</span>
        </button>

        <span class="day-display">
          <template v-if="selectedDate && endDate">
            <span class="date-range">
              <span class="desktop-format">{{
                formatHeaderDateDesktop(selectedDate)
              }}</span>
              <span class="mobile-format">{{
                formatHeaderDateMobile(selectedDate)
              }}</span>
              <span class="date-separator">-</span>
              <span class="end-date">
                <span class="desktop-format">{{
                  formatHeaderDateDesktop(endDate)
                }}</span>
                <span class="mobile-format">{{
                  formatHeaderDateMobile(endDate)
                }}</span>
              </span>
            </span>
          </template>
          <template v-else-if="selectedDate">
            <span class="desktop-format">{{
              formatHeaderDateDesktop(selectedDate)
            }}</span>
            <span class="mobile-format">{{
              formatHeaderDateMobile(selectedDate)
            }}</span>
          </template>
          <template v-else>
            <span class="desktop-format">{{ currentDateHeaderDesktop }}</span>
            <span class="mobile-format">{{ currentDateHeaderMobile }}</span>
          </template>
        </span>

        <button class="nav-arrow next-arrow" @click="navigateNext">
          <span>→</span>
        </button>
      </div>

      <div class="calendar-container">
        <!-- Left Calendar (Current Month) -->
        <div class="calendar">
          <div class="month-year">
            <CustomFilterSelectMonths
              v-model="leftMonthName"
              :options="monthNames"
              :viewedMonthName="monthNames[leftMonth]"
              :currentMonth="currentMonth"
              :currentYear="leftYear"
              @input="updateLeftMonth"
            />
            <CustomFilterSelectYearsV2
              v-model="leftYear"
              :options="availableYears"
              :currentYear="currentYear"
            />
          </div>
          <p v-if="showFlightsNotAvialableYetLeft" class="error-message">
            Flights not available yet.
          </p>
          <p v-else-if="showFlightsNotAvialableLeft" class="error-message">
            Flights not available.
          </p>
          <hr />
          <div class="day-names">
            <span v-for="day in dayNamesAbbreviated" :key="`left-${day}`">{{
              day
            }}</span>
          </div>
          <div class="days">
            <span
              v-for="date in leftDates"
              :key="`left-${dateKey(date)}`"
              :class="[
                dateClasses(date),
                {
                  'range-start': isRangeStart(date),
                  'range-end': isRangeEnd(date),
                  'in-range': isInRange(date),
                },
              ]"
              data-cy="calendar-day"
              @click="handleDateClick(date)"
            >
              {{ displayDate(date) }}
            </span>
          </div>
        </div>

        <!-- Right Calendar (Next Month) -->
        <div class="calendar">
          <div class="month-year">
            <CustomFilterSelectMonths
              v-model="rightMonthName"
              :options="monthNames"
              :viewedMonthName="monthNames[rightMonth]"
              :currentMonth="currentMonth"
              :currentYear="rightYear"
              @input="updateRightMonth"
            />
            <CustomFilterSelectYearsV2
              v-model="rightYear"
              :options="availableYears"
              :currentYear="currentYear"
            />
          </div>
          <p v-if="showFlightsNotAvialableYetRight" class="error-message">
            Flights not available yet.
          </p>
          <p v-else-if="showFlightsNotAvialableRight" class="error-message">
            Flights not available.
          </p>
          <hr />
          <div class="day-names">
            <span v-for="day in dayNamesAbbreviated" :key="`right-${day}`">{{
              day
            }}</span>
          </div>
          <div class="days">
            <span
              v-for="date in rightDates"
              :key="`right-${dateKey(date)}`"
              :class="[
                dateClasses(date),
                {
                  'range-start': isRangeStart(date),
                  'range-end': isRangeEnd(date),
                  'in-range': isInRange(date),
                },
              ]"
              data-cy="calendar-day"
              @click="handleDateClick(date)"
            >
              {{ displayDate(date) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomFilterSelectMonths from "@/components/CustomFilterSelectMonths.vue";
import CustomFilterSelectYearsV2 from "@/components/CustomFilterSelectYearsV2.vue";

export default {
  name: "CustomDatePickerContentV2",
  components: {
    CustomFilterSelectMonths,
    CustomFilterSelectYearsV2,
  },
  props: {
    disabledDates: {
      type: Array,
      default: () => [],
    },
    previousSelectedDate: {
      type: Date,
      default: null,
    },
    minDate: {
      type: Date,
      required: true,
    },
    pickupMonth: {
      type: Number,
      default: null,
    },
    pickupYear: {
      type: Number,
      default: null,
    },
    isOneWay: {
      type: Boolean,
      default: false,
    },
    previousEndDate: {
      type: Date,
      default: null,
    },
    isReturnDateField: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    const today = new Date();
    const initialDate = this.previousSelectedDate || today;

    // Initialize left month as current or provided month
    const leftMonth =
      this.pickupMonth !== null ? this.pickupMonth : initialDate.getMonth();
    const leftYear =
      this.pickupYear !== null ? this.pickupYear : initialDate.getFullYear();

    // Initialize right month as next month (or January of next year if current month is December)
    let rightMonth = leftMonth + 1;
    let rightYear = leftYear;

    if (rightMonth > 11) {
      rightMonth = 0;
      rightYear++;
    }

    return {
      selectedDate: this.previousSelectedDate || null,
      endDate: this.previousEndDate,
      selectionInProgress:
        this.previousSelectedDate && !this.previousEndDate && !this.isOneWay,
      currentDate: initialDate,
      leftMonth,
      leftYear,
      rightMonth,
      rightYear,
      leftMonthName: "",
      rightMonthName: "",
      dayNamesAbbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      dayNamesFull: [
        "Sunday",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
      ],
      monthNames: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      monthNamesShort: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ],
      availableYears: Array.from(
        { length: 2 },
        (_, i) => new Date().getFullYear() + i
      ).filter((year) => year >= new Date().getFullYear()),
    };
  },
  computed: {
    currentMonth() {
      return this.currentDate.getMonth();
    },
    currentYear() {
      return this.currentDate.getFullYear();
    },
    currentDateHeaderDesktop() {
      const today = new Date();
      const days = this.dayNamesFull;
      const months = this.monthNames;
      return `${days[today.getDay()]} ${
        months[today.getMonth()]
      } ${today.getDate()}, ${today.getFullYear()}`;
    },
    currentDateHeaderMobile() {
      const today = new Date();
      const months = this.monthNamesShort;
      return `${
        months[today.getMonth()]
      } ${today.getDate()}, ${today.getFullYear()}`;
    },
    selectedDayDisplay() {
      if (!this.selectedDate) return "";

      if (this.endDate) {
        const startStr = `${this.dayNamesFull[this.selectedDate.getDay()]} ${
          this.monthNames[this.selectedDate.getMonth()]
        } ${this.selectedDate.getDate()}, ${this.selectedDate.getFullYear()}`;

        const endStr = `${this.dayNamesFull[this.endDate.getDay()]} ${
          this.monthNames[this.endDate.getMonth()]
        } ${this.endDate.getDate()}, ${this.endDate.getFullYear()}`;

        return `${startStr} - ${endStr}`;
      }

      return `${this.dayNamesFull[this.selectedDate.getDay()]} ${
        this.monthNames[this.selectedDate.getMonth()]
      } ${this.selectedDate.getDate()}, ${this.selectedDate.getFullYear()}`;
    },
    leftDates() {
      return this.getDatesForMonth(this.leftYear, this.leftMonth);
    },
    rightDates() {
      return this.getDatesForMonth(this.rightYear, this.rightMonth);
    },
    // Add dropdownMonthOptions computed property
    dropdownMonthOptions() {
      if (this.currentYear === new Date().getFullYear()) {
        return this.monthNames.slice(new Date().getMonth());
      } else {
        return this.monthNames.slice(0, new Date().getMonth() + 1);
      }
    },

    // For left calendar
    showFlightsNotAvialableYetLeft() {
      const today = new Date();
      const futureLimitDate = new Date();
      futureLimitDate.setDate(today.getDate() + 361);
      const monthStart = new Date(this.leftYear, this.leftMonth, 1);
      return (
        monthStart > futureLimitDate &&
        this.areAllDatesInMonthDisabled(this.leftYear, this.leftMonth)
      );
    },

    showFlightsNotAvialableLeft() {
      const isPastMonth = this.isMonthInPast(this.leftYear, this.leftMonth);
      return (
        isPastMonth &&
        this.areAllDatesInMonthDisabled(this.leftYear, this.leftMonth)
      );
    },

    // For right calendar
    showFlightsNotAvialableYetRight() {
      const today = new Date();
      const futureLimitDate = new Date();
      futureLimitDate.setDate(today.getDate() + 361);
      const monthStart = new Date(this.rightYear, this.rightMonth, 1);
      return (
        monthStart > futureLimitDate &&
        this.areAllDatesInMonthDisabled(this.rightYear, this.rightMonth)
      );
    },

    showFlightsNotAvialableRight() {
      const isPastMonth = this.isMonthInPast(this.rightYear, this.rightMonth);
      return (
        isPastMonth &&
        this.areAllDatesInMonthDisabled(this.rightYear, this.rightMonth)
      );
    },
  },
  watch: {
    leftMonth(newMonth) {
      // If left month is December, right month should be January of next year
      if (newMonth === 11) {
        this.rightMonth = 0;
        this.rightYear = this.leftYear + 1;
      } else {
        // Otherwise, right month is next month of same year
        this.rightMonth = newMonth + 1;
        this.rightYear = this.leftYear;
      }
      this.leftMonthName = this.monthNames[newMonth];
    },
    leftYear(newYear) {
      // Ensure right year is correct when left year changes
      if (this.leftMonth === 11) {
        this.rightYear = newYear + 1;
      } else {
        this.rightYear = newYear;
      }
    },
    rightMonth(newMonth) {
      this.rightMonthName = this.monthNames[newMonth];
    },
    previousSelectedDate(newVal) {
      if (this.isValidDefaultDate(newVal)) {
        this.selectedDate = new Date(newVal);
        this.currentDate = new Date(newVal);

        // Update months if necessary
        this.leftMonth = this.selectedDate.getMonth();
        this.leftYear = this.selectedDate.getFullYear();
      }
    },
  },
  methods: {
    isMobile() {
      return window.innerWidth <= 780;
    },

    navigatePrevious() {
      // Move by 1 month on mobile, 2 months on desktop
      const step = this.isMobile() ? 1 : 2;
      let newLeftMonth = this.leftMonth - step;
      let newLeftYear = this.leftYear;

      if (newLeftMonth < 0) {
        newLeftMonth = 12 + newLeftMonth; // Add 12 to get the correct month
        newLeftYear--;
      }

      this.leftMonth = newLeftMonth;
      this.leftYear = newLeftYear;
    },

    navigateNext() {
      // Move by 1 month on mobile, 2 months on desktop
      const step = this.isMobile() ? 1 : 2;
      let newLeftMonth = this.leftMonth + step;
      let newLeftYear = this.leftYear;

      if (newLeftMonth > 11) {
        newLeftMonth = newLeftMonth - 12; // Subtract 12 to get the correct month
        newLeftYear++;
      }

      this.leftMonth = newLeftMonth;
      this.leftYear = newLeftYear;
    },

    updateLeftMonth(monthName) {
      const monthIndex = this.monthNames.indexOf(monthName);
      if (monthIndex !== -1) {
        this.leftMonth = monthIndex;
      }
    },

    updateRightMonth(monthName) {
      const monthIndex = this.monthNames.indexOf(monthName);
      if (monthIndex !== -1) {
        if (
          this.rightYear < this.leftYear ||
          (this.rightYear === this.leftYear && monthIndex <= this.leftMonth)
        ) {
          let newLeftMonth = monthIndex - 1;
          let newLeftYear = this.rightYear;

          if (newLeftMonth < 0) {
            newLeftMonth = 11; // December
            newLeftYear--;
          }

          this.leftMonth = newLeftMonth;
          this.leftYear = newLeftYear;
          this.leftMonthName = this.monthNames[newLeftMonth];
        }

        this.rightMonth = monthIndex;
        this.rightMonthName = this.monthNames[monthIndex];
      }
    },

    getDatesForMonth(year, month) {
      let daysInMonth = new Date(year, month + 1, 0).getDate();
      let firstDayOfMonth = new Date(year, month, 1).getDay();
      let dates = [];

      // Days of the preceding month
      let precedingMonthLastDay = new Date(year, month, 0).getDate();
      for (let i = 0; i < firstDayOfMonth; i++) {
        dates.unshift({
          date: precedingMonthLastDay - i,
          month: month - 1,
          year: month === 0 ? year - 1 : year,
          disabled: true,
        });
      }

      // Actual days of the month
      for (let i = 1; i <= daysInMonth; i++) {
        let currentDateObj = new Date(year, month, i);
        dates.push({
          date: i,
          month: month,
          year: year,
          disabled: this.isDateDisabled(currentDateObj),
        });
      }

      // Days of the subsequent month
      let counter = 1;
      while (dates.length % 7 !== 0) {
        dates.push({
          date: counter,
          month: month + 1 === 12 ? 0 : month + 1,
          year: month + 1 === 12 ? year + 1 : year,
          disabled: true,
        });
        counter++;
      }

      return dates;
    },

    isMonthInPast(year, month) {
      const today = new Date();
      const firstOfCurrentMonth = new Date(
        today.getFullYear(),
        today.getMonth(),
        1
      );
      const monthToCheck = new Date(year, month, 1);
      return monthToCheck < firstOfCurrentMonth;
    },

    areAllDatesInMonthDisabled(year, month) {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month, day);
        if (!this.isDateDisabled(date)) {
          return false;
        }
      }
      return true;
    },

    isSelected(date) {
      return (
        this.selectedDate &&
        this.selectedDate.getDate() === date.date &&
        this.selectedDate.getMonth() === date.month &&
        this.selectedDate.getFullYear() === date.year
      );
    },

    isRangeStart(date) {
      if (date.disabled) return false;
      return this.isSelected(date);
    },

    isRangeEnd(date) {
      if (date.disabled) return false;
      return (
        this.endDate &&
        this.endDate.getDate() === date.date &&
        this.endDate.getMonth() === date.month &&
        this.endDate.getFullYear() === date.year
      );
    },

    isInRange(date) {
      if (!this.selectedDate || !this.endDate || !date) return false;

      // Remove the visual date range between selected dates
      return false;
    },

    isDateDisabled(date) {
      // Set the limit date to 350 days in the future
      const today = new Date();
      const futureLimitDate = new Date();
      futureLimitDate.setDate(today.getDate() + 361);

      // Disable the date if it's more than 361 days in the future
      if (date > futureLimitDate) {
        return true;
      }

      // Check if it's today's date. If yes, then it shouldn't be disabled
      if (this.isToday(date)) {
        return false;
      }

      // Check if date is before minDate
      const strippedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
      const strippedMinDate = new Date(
        this.minDate.getFullYear(),
        this.minDate.getMonth(),
        this.minDate.getDate()
      );

      if (strippedDate < strippedMinDate) {
        return true;
      }

      // For range selection - if start date is selected, disable dates before it
      if (this.selectionInProgress && this.selectedDate) {
        if (strippedDate < this.selectedDate) {
          return true;
        }
      }

      // Check against disabled date ranges
      for (let range of this.disabledDates) {
        let start = range.start || new Date(0);
        start = new Date(
          start.getFullYear(),
          start.getMonth(),
          start.getDate()
        );

        let end = range.end || new Date();
        end = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (strippedDate >= start && strippedDate <= end) {
          return true;
        }
      }
      return false;
    },

    handleDateClick(date) {
      if (date && !date.disabled) {
        const selectedDateObj = new Date(date.year, date.month, date.date);

        // One-way flight - just select the date and close
        if (this.isOneWay) {
          this.selectedDate = selectedDateObj;
          this.applyDate();
          return;
        }

        // Return date field specific behavior
        if (this.isReturnDateField) {
          // For return date field, only update the return date
          // Keep the existing departure date and just update the return date
          if (this.selectedDate) {
            // Ensure the new return date is not before the departure date
            if (selectedDateObj >= this.selectedDate) {
              this.endDate = selectedDateObj;
            } else {
              // If user selects a date before departure, swap them
              this.endDate = this.selectedDate;
              this.selectedDate = selectedDateObj;
            }
            this.selectionInProgress = false;
            this.applyDateRange();
          } else {
            // No departure date set yet, treat as normal range selection
            this.selectedDate = selectedDateObj;
            this.endDate = null;
            this.selectionInProgress = true;
          }
          return;
        }

        // For departure date field - always allow range selection
        // First time interaction or departure field interaction
        if (!this.selectedDate || !this.selectionInProgress) {
          // First click - select start date only
          this.selectedDate = selectedDateObj;
          this.endDate = null;
          this.selectionInProgress = true;

          // Don't auto-apply yet
        } else {
          // Second click - select end date and apply
          if (selectedDateObj < this.selectedDate) {
            // If second date is before first date, swap them
            this.endDate = this.selectedDate;
            this.selectedDate = selectedDateObj;
          } else {
            this.endDate = selectedDateObj;
          }
          this.selectionInProgress = false;
          this.applyDateRange();
        }
      }
    },

    formatHeaderDateDesktop(date) {
      if (!date) return "";
      const day = this.dayNamesAbbreviated[date.getDay()];
      const month = this.monthNames[date.getMonth()];
      return `${day} ${month} ${date.getDate()}, ${date.getFullYear()}`;
    },

    formatHeaderDateMobile(date) {
      if (!date) return "";
      const month = this.monthNamesShort[date.getMonth()];
      return `${month} ${date.getDate()}, ${date.getFullYear()}`;
    },

    applyDate() {
      if (this.selectedDate) {
        this.$emit("updateDate", this.selectedDate);
      }
    },

    applyDateRange() {
      if (this.selectedDate && this.endDate) {
        // Add this validation before emitting
        let startDate = this.selectedDate;
        let endDate = this.endDate;
        if (endDate < startDate) {
          [startDate, endDate] = [endDate, startDate];
        }
        this.$emit("updateDateRange", { startDate, endDate });
      }
    },

    isToday(date) {
      const today = new Date();
      return (
        today.getDate() === date.date &&
        today.getMonth() === date.month &&
        today.getFullYear() === date.year
      );
    },

    isValidDefaultDate(date) {
      if (!date) {
        return new Date() >= this.minDate;
      }
      return date >= this.minDate && !this.isDateDisabled(date);
    },

    dateKey(date) {
      return date ? `${date.date}-${date.month}-${date.year}` : "placeholder";
    },

    dateClasses(date) {
      let dayOfWeek = new Date(date.year, date.month, date.date).getDay();
      return {
        "disabled-date": date && date.disabled,
        today: this.isToday(date),
        selected: this.isSelected(date) || this.isRangeEnd(date),
        "range-start": this.isRangeStart(date),
        "range-end": this.isRangeEnd(date),
        "in-range": this.isInRange(date),
        weekend: dayOfWeek === 0 || dayOfWeek === 6,
      };
    },

    displayDate(date) {
      return date ? (date.date < 10 ? "0" + date.date : date.date) : "";
    },
  },
  mounted() {
    if (this.previousSelectedDate) {
      this.selectedDate = new Date(this.previousSelectedDate);
    }

    if (
      this.previousSelectedDate &&
      this.previousEndDate &&
      this.previousEndDate < this.previousSelectedDate
    ) {
      this.selectedDate = new Date(this.previousEndDate);
      this.endDate = new Date(this.previousSelectedDate);
    }

    // Initialize month names
    this.leftMonthName = this.monthNames[this.leftMonth];
    this.rightMonthName = this.monthNames[this.rightMonth];

    // Set initial starting month to be today's month or the selected date's month if provided
    if (this.selectedDate) {
      this.leftMonth = this.selectedDate.getMonth();
      this.leftYear = this.selectedDate.getFullYear();
    } else {
      const today = new Date();
      this.leftMonth = today.getMonth();
      this.leftYear = today.getFullYear();
    }
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/settings.scss";

.datepicker {
  border: none;
  width: 100%;
  max-width: 100%;
  background-color: #fff;
  box-sizing: border-box;
  padding: 12px;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  line-height: 24px;
}

.header {
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  flex-direction: column;
}

.navigation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  width: 100%;
}

.day-display {
  display: flex;
  justify-content: center;
  font-weight: bold;
  font-size: 16px;
  padding: 10px;
}

.calendar-container {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.calendar {
  flex: 1;
}

.month-year {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
}

.error-message {
  padding-top: 5px;
  padding-left: 5px;
  color: red !important;
  font-size: smaller;
  margin: 0;
}

.day-names,
.days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  grid-row-gap: 8px;
  margin: 0;
  margin-bottom: 16px;
  box-sizing: border-box;
}

.days span {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.days span,
.day-names span {
  width: 32px;
  height: 32px;
  padding: 8px;
  border: none;
  box-sizing: border-box;
  font-size: 12px;
  text-align: center;
  cursor: pointer;
}

.days span:hover:not(.disabled-date) {
  background-color: #f0f0f0;
}

//Disable hover effects on touch devices to prevent mobile issues
@media (hover: none) {
  .days span:hover:not(.disabled-date) {
    background-color: initial;
  }

  // Disable hover effects on navigation arrows for touch devices
  .nav-arrow:hover {
    background: #30384a !important;
  }
}

.days span.today {
  background-color: #f9f3eb;
  color: #964e1e;
}

.days span.selected,
.days span.range-start,
.days span.range-end {
  background-color: #ac6411 !important;
  color: #ffffff !important;
  font-weight: bold;
  border-radius: 50%;
}

.days span.in-range {
  background-color: #f9f3eb;
  border-radius: 0;
}

.day-names span {
  border: 1px solid transparent;
}

.disabled-date {
  color: #999;
  pointer-events: none;
  cursor: not-allowed;
}

.day-names span:nth-child(1),
.day-names span:nth-child(7) {
  font-weight: bold;
}

.days span.weekend {
  font-weight: bold;
}

.nav-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #30384a;
  color: white;
  border: none;
  width: 60px;
  height: 35px;
  font-size: 25px;
  cursor: pointer;
  align-self: center;
}

.nav-arrow span {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-style: normal;
  font-weight: normal;
}

.prev-arrow,
.next-arrow {
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 8px;
}

.nav-arrow:hover {
  background: #485dc5;
}

.mobile-format {
  display: none;
}

.desktop-format {
  display: inline;
}

.date-range {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
}

.date-separator {
  margin: 10px;
}

/* Mobile responsive adjustments */
@media (max-width: 780px) {
  .calendar-container {
    flex-direction: column;
    gap: 30px;
  }

  .nav-arrow {
    margin: 10px 0;
  }

  .date-range {
    flex-direction: column;
    align-items: center;
    flex-wrap: wrap;
  }

  .date-separator {
    padding: 0px;
  }

  .mobile-format {
    display: inline;
  }

  .desktop-format {
    display: none;
  }

  // Fix mobile hover state issue - prevent hover from overriding selected state
  .days span:hover:not(.disabled-date) {
    background-color: initial;
  }

  .days span.selected:hover,
  .days span.range-start:hover,
  .days span.range-end:hover {
    background-color: #ac6411 !important;
    color: #ffffff !important;
  }
}
</style>
